import React, { useCallback, useMemo, memo, useState, useEffect, useRef } from 'react';
import { useMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveInfoWindowId } from '@/store/slices/mapUiSlice';
import CustomMarker from './LocationMarker';
import { selectVisibleGeoJsonPoints, selectMapViewport } from '@/store/selectors/mapSelectors';
import useSupercluster, { PointProps, ClusterProps, CombinedFeature } from '@/hooks/useSupercluster';
import { padBounds } from '@/utils/geo';
import { useTheme } from 'next-themes';

// Type guard to check if properties are cluster properties
function isClusterProperties(
  props: PointProps | ClusterProps
): props is ClusterProps {
  // Check if the 'cluster' property exists and is true
  return 'cluster' in props && props.cluster === true;
}

// Helper to parse "oklch(L C H)" or "oklch(L C H / A)"
// Expects L as 0-1, C as number (approx 0-0.5), H as degrees (0-360), A as 0-1
function parseOklch(colorString: string | null): { l: number, c: number, h: number, a?: number } | null {
  if (!colorString) return null;
  // Regex to capture L, C, H and optional Alpha from oklch() string
  const oklchMatch = colorString.match(
    /oklch\(\s*([0-9.]+)\s+([0-9.]+)\s+([0-9.]+)(?:\s*\/\s*([0-9.]+))?\s*\)/
  );
  if (oklchMatch) {
    return {
      l: parseFloat(oklchMatch[1]),
      c: parseFloat(oklchMatch[2]),
      h: parseFloat(oklchMatch[3]),
      a: oklchMatch[4] ? parseFloat(oklchMatch[4]) : undefined, // Alpha is optional
    };
  }
  return null; // Return null if parsing fails
}

// Helper to interpolate between two OKLCH colors
function interpolateOklch(
  color1: { l: number, c: number, h: number, a?: number },
  color2: { l: number, c: number, h: number, a?: number },
  t: number // Interpolation factor (0.0 to 1.0)
): string {
  const l = color1.l * (1 - t) + color2.l * t;
  const c = color1.c * (1 - t) + color2.c * t;

  // Hue interpolation (shortest path around the color wheel)
  const h1 = color1.h;
  let h2 = color2.h;
  let deltaH = h2 - h1;

  if (deltaH > 180) {
    h2 -= 360;
  } else if (deltaH < -180) {
    h2 += 360;
  }
  deltaH = h2 - h1; // Recalculate deltaH with the adjusted h2
  let h = (h1 + t * deltaH);
  h = ((h % 360) + 360) % 360; // Normalize H to be 0-359.99...

  // Alpha interpolation (defaulting to 1 if undefined)
  const a1 = color1.a === undefined ? 1 : color1.a;
  const a2 = color2.a === undefined ? 1 : color2.a;
  const a = a1 * (1 - t) + a2 * t;

  // Construct the oklch() string
  // Omit alpha if it's effectively 1 (within a small tolerance)
  if (Math.abs(a - 1) < 0.001) {
    return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)})`;
  } else {
    return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)} / ${a.toFixed(2)})`;
  }
}

const MAX_POINTS_FOR_GRADIENT = 120; // Max point_count for full "destructive" color

// Memoized cluster component to prevent re-renders
interface ClusterMarkerProps {
  clusterId: number; 
  pointCount: number; 
  position: { lat: number; lng: number }; 
  onClusterClick: (lng: number, lat: number) => void; 
}

const ClusterMarkerComponent = ({
  pointCount,
  position,
  onClusterClick,
}: ClusterMarkerProps) => {
  const { theme: currentThemeName } = useTheme(); // For re-fetching colors on theme change
  const [isAnimating, setIsAnimating] = useState(true);

  const [basePrimaryColor, setBasePrimaryColor] = useState<{ l: number, c: number, h: number, a?: number } | null>(null);
  const [baseDestructiveColor, setBaseDestructiveColor] = useState<{ l: number, c: number, h: number, a?: number } | null>(null);

  useEffect(() => {
    // Fetch and parse theme colors when component mounts or theme changes
    if (typeof window !== 'undefined') {
      const primaryCss = getComputedStyle(document.documentElement).getPropertyValue('--chart-2').trim();
      const destructiveCss = getComputedStyle(document.documentElement).getPropertyValue('--destructive').trim();
      
      setBasePrimaryColor(parseOklch(primaryCss));
      setBaseDestructiveColor(parseOklch(destructiveCss));
    }
  }, [currentThemeName]);

  // Size and color based on point count
  const size = React.useMemo(() => {
    return Math.min(80, 40 + (pointCount / Math.max(100, pointCount * 2)) * 40);
  }, [pointCount]);

  // Color scale from primary -> destructive based on count
  const color = React.useMemo(() => {
    if (basePrimaryColor && baseDestructiveColor) {
      const t = Math.min(1, pointCount / MAX_POINTS_FOR_GRADIENT);
      return interpolateOklch(basePrimaryColor, baseDestructiveColor, t);
    }
    // Fallback to previous stepped logic if oklch parsing failed or colors not loaded
    if (pointCount < 10) {
      return 'var(--chart-5)'; 
    } else if (pointCount < 50) {
      return 'var(--accent)'; 
    } else if (pointCount < 100) {
      return 'var(--chart-4)';
    } else {
      return 'var(--destructive)';
    }
  }, [pointCount, basePrimaryColor, baseDestructiveColor]);

  const handleClick = useCallback(() => {
    onClusterClick(position.lng, position.lat);
  }, [position.lng, position.lat, onClusterClick]);

  // Animation effect (copied from LocationMarker)
  useEffect(() => {
    if (!isAnimating) return; // Skip if already done animating

    // Use RAF for smoother animation aligned with browser rendering
    const startTime = performance.now();
    const duration = 300; // Animation duration in ms

    const animationFrame = requestAnimationFrame(function animate(time) {
      const elapsed = time - startTime;

      if (elapsed >= duration) {
        // Animation complete
        setIsAnimating(false);
        return;
      }

      // Continue animation
      requestAnimationFrame(animate);
    });

    // Clean up animation frame on unmount
    return () => cancelAnimationFrame(animationFrame);
  }, [isAnimating, position.lng, position.lat, onClusterClick]); // Depends on position and onClusterClick

  return (
    <AdvancedMarker
      position={{ lat: position.lat, lng: position.lng }}
      onClick={handleClick}
    >
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: color,
          boxShadow: '0 0 0 4px white',
          fontSize: `${Math.min(22, 14 + (pointCount / 100) * 8)}px`,
          fontWeight: 'bold',
          color: 'white',
          // Ensure transition covers transform and opacity
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', // Use same easing as LocationMarker
          border: '2px solid rgba(255,255,255,0.6)',
          // Animation styles
          transform: isAnimating ? 'scale(0)' : 'scale(1)',
          opacity: isAnimating ? 0 : 1,
        }}
      >
        {pointCount}
      </div>
    </AdvancedMarker>
  );
};

// Custom comparator: ignore onClusterClick reference changes
const areClusterMarkerPropsEqual = (prev: ClusterMarkerProps, next: ClusterMarkerProps): boolean => {
  // Compare rounded coordinates directly
  return (
    prev.pointCount === next.pointCount &&
    prev.position.lng === next.position.lng && // Already rounded, strict comparison is fine
    prev.position.lat === next.position.lat  // Already rounded, strict comparison is fine
    // onClusterClick is ignored by this custom function
  );
};

const ClusterMarker = memo(ClusterMarkerComponent, areClusterMarkerPropsEqual);

ClusterMarker.displayName = 'ClusterMarker';

// --- Main Markers Component --- 
const Markers: React.FC = () => {
    const map = useMap();
    const mapRef = useRef<google.maps.Map | null>(null);
    const dispatch = useAppDispatch();
    
    useEffect(() => {
      if (map) {
        mapRef.current = map;
      }
    }, [map]);

    // --- New Data Flow using Selectors and Hook --- 
    const points = useAppSelector(selectVisibleGeoJsonPoints);
    const { bounds: mapBounds, zoom } = useAppSelector(selectMapViewport);
    
    // Calculate padded bounds for the hook. Factor 0.5 = 50% padding.
    const paddedBounds = useMemo(() => padBounds(mapBounds, 0.5), [mapBounds]);
    
    // Use the supercluster hook to get features to render
    const clusters = useSupercluster(points, zoom, paddedBounds);
    
    // Handle cluster click (Keep logic)
    const handleClusterClick = useCallback((lng: number, lat: number) => {
      if (!mapRef.current) return;
      // Assume max zoom or fixed increment
      const expansionZoom = Math.min(zoom + 2, 20); // Zoom in 2 levels
      mapRef.current.setZoom(expansionZoom);
      mapRef.current.panTo({ lat, lng });
    }, [zoom]);
    
    // Handle marker click (Use dispatch)
    const handleMarkerClick = useCallback((markerId: string) => {
      dispatch(setActiveInfoWindowId(markerId));
    }, []);
    
    // Use a stable wrapper function to prevent re-renders
    const renderFeatures = useCallback((features: CombinedFeature[]) => {
      if (!features || features.length === 0) return null;
      
      return features.map(feature => {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties;

        if (isClusterProperties(props)) {
          // --- Render Cluster --- 
          const clusterProps = props as ClusterProps; // Type cast for clarity
          const key = clusterProps.key!; // Use the stable key generated by hook
          const position = { lat, lng }; // Create position object with original coordinates

          return (
            <ClusterMarker
              key={key} // Use the generated key
              clusterId={clusterProps.cluster_id} 
              pointCount={clusterProps.point_count}
              position={position} // Pass position object
              onClusterClick={handleClusterClick}
            />
          ); // End ClusterMarker return
        } else {
          // --- Render Single Point --- 
          // Properties are PointProps in this block
          const pointProps = props as PointProps; // Cast might still be useful for clarity
          const key = pointProps.key!; // Key is guaranteed by hook
          
          // For single points, the key is just p-id, use original coordinates
          const position = { lat, lng };

          // Use ID from properties for the click handler
          const clickHandler = pointProps.id
            ? () => handleMarkerClick(pointProps.id)
            : undefined;

          // Pass primitive data and stable handler to CustomMarker
          return <CustomMarker
            key={`${key}-${pointProps.locationType}`} // Include locationType in key to force re-render when type changes
            locationId={pointProps.id}
            pointProps={pointProps} // Pass the properties object
            lat={position.lat} // Use consistent position object
            lng={position.lng}
            onClick={clickHandler}
          />;
        }
      });
    }, [handleClusterClick, handleMarkerClick]);

    // Only recreate the rendered markers when clusters array reference changes
    const renderedMarkers = useMemo(() => {
      return renderFeatures(clusters);
    }, [clusters, renderFeatures]);
    
    return <>{renderedMarkers}</>;
};

// Create a proper memo comparison function to prevent re-renders
// The component should only re-render when clusters array identity changes
const MemoizedMarkers = memo(Markers, () => {
  // No props to compare now, but this follows the pattern if props are added later
  return true; // Rely on internal hook dependencies for re-renders
});

// Add displayName for debugging
MemoizedMarkers.displayName = 'MemoizedMarkers';

export default MemoizedMarkers;